/* eslint-disable no-unsafe-optional-chaining */
import React, { useContext, useState } from "react";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import { useEffectOnce } from "@lib-shared/hooks";
import { LookupPlaceholders } from "core/constants";
import { ModalContext } from "core/context/modal.context";
import { getErrorMessage, logApiError } from "core/lib";
import { getContactEmail, getContactNumber, getContactTimesHtml } from "core/lib/html-helpers";
import ClearErrorService, { ClearErrorRequestResponse } from "core/services/cef/clear-error.service";
import ClearErrorStore from "./clear-error.store";
import { ErrorCode } from "./clear-error.store.manager";
import { ValidationMessage } from "components/common-ui";
import SelectX, { Option } from "components/ui/form/select";

type ClearErrorModalProps = {
  onCloseHandler?: any;
  onSubmitSuccessHandler: any;
  onSubmitFailedHandler: any;
  onSubmitErrorHandler: any;
  modalClosedHandler: any;
  openModal: any;
  store: ClearErrorStore;
}

const ClearErrorModal = observer((props: ClearErrorModalProps) => {
  const {
    store,
    openModal,
    onSubmitSuccessHandler,
    onSubmitErrorHandler,
    onSubmitFailedHandler,
    modalClosedHandler
  } = props;

  if (!store) {
    throw new Error("Store is not defined.");
  }

  const [currentError, setCurrentError] = useState<any>(null);
  const [modalOpen, setModalIsOpen] = useState(false);
  const [showDescription, setShowDescription] = useState<boolean>(false);

  const modalContext = useContext(ModalContext);

  const defaultErrorMessage = "Error occurred in api";
  const failureMessage = "Sorry your request has failed";

  const replaceSmartcardPlaceholder = (text: string) => {
    const replacement = `<strong>${store.smartCardNumber ?? "Enter Smartcard number"}</strong>`;
    return text.replace(LookupPlaceholders.smartcard, replacement);
  };

  const setModalState = (show: boolean) => {
    if (modalContext === null) {
      return;
    }

    if (show) {
      modalContext.show();
      return;
    }

    modalContext.close();
  }

  //open modal
  useEffectOnce(() => {
    setModalIsOpen(openModal);

    setModalState(openModal);
  });

  const clearErrorApi = new ClearErrorService();

  const fixError = async (e: any) => {
    if (!store.isValid) {
      return;
    }

    modalClosedHandler();

    //No longer requiring smartcard validation/Apis validate smartcard
    const handleResponse = (response: ClearErrorRequestResponse) => {
      const checkStatus = () =>
        setTimeout(async () => {
          try {
            const { id, distributionPlatform } = response.clearErrorResponse[0];
            const statusResponse = await clearErrorApi.status(id, distributionPlatform);
            const statusData = statusResponse.data.getStatusResponse[0];

            switch (statusData.status) {
              case "success":
                onSubmitSuccessHandler(true);
                break;

              case "pending":
                checkStatus();
                break;

              case "failed":
                onSubmitFailedHandler(statusData.detail ?? failureMessage);
                break;

              default:
                onSubmitErrorHandler(statusData.detail ?? defaultErrorMessage);
            }
          } catch (error) {
            let errorMessage = getErrorMessage(error);

            errorMessage = errorMessage.includes("[ISS") ? defaultErrorMessage : errorMessage;

            onSubmitErrorHandler(errorMessage);
          }
        }, 2000);

      checkStatus();
    };

    const handleError = (error: any) => {
      logApiError(error);

      const data = error?.response?.data;

      let message =
        error?.response?.status === 404
          ? "Smartcard number is not valid or associated with your account"
          : data?.errorResponse?.errorDescription ?? defaultErrorMessage;

      message = message.includes("KomodoQueryAPI") ? defaultErrorMessage : message;

      setShowDescription(false);

      onSubmitErrorHandler(message);
    };

    try {
      const response = await clearErrorApi.clear(store.smartCardNumber ?? "", store.selectedErrorCode?.code ?? "");
      handleResponse(response?.data);
    } catch (error) {
      handleError(error);
    }
  };

  const onSmartCardChangeHandler = (e: any) => {
    const smartCardNumber = e.target.value;

    store.updateSmartCardNumber(smartCardNumber);

    createCurrentErrorDetails(store.selectedErrorCode);
  };

  const createCurrentErrorDetails = (code: ErrorCode | undefined) => {
    if (!code) {
      setCurrentError(null);
      return;
    }

    store.updateSelectedErrorCode(code.code);

    //Preserve original object with placeholder, in case user changes smartcard number
    const shallowCopyOfErrorCode = {
      ...code,
      title: replaceSmartcardPlaceholder(code.title),
      description: replaceSmartcardPlaceholder(code.description),
    };

    setCurrentError(shallowCopyOfErrorCode);
  }

  const onErrorCodeChangeHandler = (option?: Option) => {
    const found = store.updateSelectedErrorCode(option?.value);

    createCurrentErrorDetails(found);
  };

  const onCancelHandler = (e: any) => {
    store.reset(false);

    if (modalContext) {
      setModalState(false);
    }

    e.preventDefault();
    setModalIsOpen(false);
    modalClosedHandler(false);
  };

  const populateDangerousHtml = (html: string) => {
    return { __html: html };
  };

  const names = {
    columns: {
      smartcard: "smartcard-col",
      code: "errorcode-col"
    },
    ids: {
      smartcard: "smartcard-number",
      code: "errorcode"
    }
  }

  const options = store.errorCodes.map(x => {
    return { label: x.title, value: x.code };
  });

  const SidebarBody = () => {
    if (showDescription) {
      const email = getContactEmail();
      return (
        <p>
          If you still experience the error after trying to clear it, call us on {getContactNumber()} between {getContactTimesHtml()} or <a href={`mailto:${email}`} title={email}>email us</a>
        </p>
      );
    }

    return (
      <>
        <p>
          <strong>Simple steps to solve your DStv errors:</strong>
        </p>

        <ol>
          <li>
            Enter your <strong>Smartcard number</strong>
          </li>
          <li>
            Choose the <strong>error code</strong> you see on screen
          </li>
          <li>
            Click on the <strong>Fix Error</strong> button
          </li>
        </ol>
      </>
    );
  }

  const BackNextButtons = () => {
    const submitCssName = "btn btn-black" + (!store.isValid ? " disabled" : "");

    const firstPage = !showDescription;

    const onCancel = (e: any) => {

      if (firstPage) {
        onCancelHandler(e);
        return;
      }

      setShowDescription(false);
    }

    const cancelButtonText = firstPage ? "Cancel" : "Back";

    const onSubmit = (e: any) => {
      if (firstPage) {
        setShowDescription(true);
        return;
      }

      fixError(e);
    }
    const submitButtonText = firstPage ? "Next" : "Fix error";

    return (
      <div className="row modal-buttons-row">
        <button
          type="button"
          className="btn btn-outline-black" onClick={onCancel}>
          {cancelButtonText}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!store.isValid}
          className={submitCssName}>
          {submitButtonText}
        </button>
      </div>
    );
  }

  const ClearErrorBody = () => {
    if (showDescription) {
      return null;
    }

    return <>
      <div className="modal-header-row">
        <h5>Let's get your TV fixed</h5>
        <button type="button" className="close">
          <i className="icon-close close" onClick={onCancelHandler}></i>
        </button>
      </div>

      <div className="row col">
        <input
          type="number"
          step={1}
          min={0}
          placeholder="Enter your Smartcard Number"
          id={names.ids.smartcard}
          name={names.ids.smartcard}
          value={store.smartCardNumber}
          onChange={onSmartCardChangeHandler}
        />

        <label htmlFor={names.ids.smartcard} className={store.smartcardNumberIsInvalid ? "error-message" : ""}>
          Smartcard Number
        </label>

        <ValidationMessage valid={!store.smartcardNumberIsInvalid} message="Please ensure your input is a 10-digit number" />
      </div>

      <div className="row col react-select">
        <SelectX
          name={names.ids.code}
          options={options}
          onHandleChange={onErrorCodeChangeHandler}
          initialValue={currentError?.code}
          label="Select an Error Code" />
      </div>

      <p className="disclaimer">
        <b>For older decoder models:</b><br />
        Make sure that your smartcard is in the decoder and that the decoder is on to get a signal.
      </p>
    </>
  }

  const ClearErrorDescription = () => {
    if (!showDescription) {
      return null;
    }

    return <>
      <div className="modal-header-row">
        <h5>{currentError.title}</h5>
      </div>

      <div className="description">
        <p dangerouslySetInnerHTML={populateDangerousHtml(currentError.description)}></p>
      </div>
    </>
  }


  return (
    <section className="modal-body">
      <form className="clear-error">
        <ClearErrorBody />

        <ClearErrorDescription />

        <BackNextButtons />
      </form>

      <div className="modal-side-bar">
        <b>Please note:</b>

        <p>
          Certain errors may not be fixable online such as errors caused by things like bad weather or other outside factors.
        </p>
        <SidebarBody />
      </div>
    </section>);
});

export default ClearErrorModal;
